// 결제수단 변경 알림톡 "결제수단 변경하기" 버튼 착지 페이지 — https://{host}/u/pm.
//
// 앱이 설치+App Links 검증된 안드로이드 단말이면 OS가 이 경로를 가로채 앱의 결제수단 관리
// 화면으로 곧장 연다(idpMobile AndroidManifest의 autoVerify App Links: pathPrefix=/u/,
// AppLandingPage.tsx·ReferralLandingPage.tsx와 동일 인프라 재사용, 2026-09-22).
// 이 페이지에 닿았다는 것은 "앱이 없거나(iOS·미설치) PC"라는 뜻이다.
//
// - PC(detectPlatform()==='other')면 굳이 물어볼 것 없이 곧장 웹 관리 화면으로 보낸다.
// - 모바일 브라우저(안드로이드·iOS)면 앱 설치 안내를 보여주되, 그 자리에서 웹으로도 계속할 수
//   있게 "웹에서 계속하기" 버튼을 같이 준다(AppLandingPage와 달리 이 페이지는 "설치 유도"가
//   아니라 "지금 당장 결제수단을 바꿔야 하는" 실사용 목적이 있어 웹 폴백이 반드시 필요하다).
//
// 인증·API 호출은 하지 않는다 — 실제 로그인 확인은 이동 대상 페이지(/agents/billing/payment-method)의
// 기존 RequireAuth·withOwnerGuard가 그대로 처리한다.

import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { detectPlatform } from '../lib/platform';

const WEB_TARGET = '/agents/billing/payment-method';
const APP_RELEASED = false; // AppLandingPage.tsx와 동일 플래그 — 앱 마켓 출시 시 true로.
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ai.indipia.app';

export function PaymentMethodLandingPage() {
  const navigate = useNavigate();
  const platform = detectPlatform();
  const showStore = APP_RELEASED && platform === 'android';

  useEffect(() => {
    if (platform === 'other') {
      navigate(WEB_TARGET, { replace: true });
    }
  }, [platform, navigate]);

  if (platform === 'other') return null;

  return (
    <div
      style={{
        minHeight: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: '32px 24px',
        background: 'var(--shell-bg)',
        color: 'var(--shell-on-surface)',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans KR", sans-serif',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          boxSizing: 'border-box',
          background: 'var(--shell-surface)',
          border: '1.5px solid var(--shell-border)',
          borderRadius: 16,
          padding: '32px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          boxShadow: '0 6px 24px var(--alpha-black-50)',
        }}
      >
        <div
          style={{
            width: 56, height: 56, borderRadius: 14, background: 'var(--indipia-purple-50)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--indipia-purple-600)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
          </svg>
        </div>

        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.5px', textAlign: 'center' }}>
          결제수단 관리
        </div>

        <div style={{ fontSize: 14, color: 'var(--shell-on-surface-2)', lineHeight: 1.7, textAlign: 'center' }}>
          인디피아 앱에서 결제수단을 더 편하게
          <br />
          등록·변경할 수 있습니다
        </div>

        {showStore && (
          <a
            href={PLAY_STORE_URL}
            style={{
              marginTop: 6, width: '100%', boxSizing: 'border-box',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '14px 20px', borderRadius: 12,
              background: 'var(--indipia-purple-600)', color: 'var(--gray-0)',
              fontSize: 15, fontWeight: 700, textDecoration: 'none',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Play 스토어에서 설치
          </a>
        )}

        {!showStore && (
          <div
            style={{
              marginTop: 6,
              width: '100%',
              boxSizing: 'border-box',
              padding: '14px 18px',
              borderRadius: 12,
              background: 'var(--shell-bg)',
              border: '1.5px solid var(--shell-border)',
              fontSize: 13.5,
              color: 'var(--shell-on-surface-2)',
              lineHeight: 1.7,
              textAlign: 'center',
            }}
          >
            앱을 준비하고 있습니다.
            <br />
            정식 출시 전까지 조금만 기다려 주세요.
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate(WEB_TARGET, { replace: true })}
          style={{
            marginTop: 4, width: '100%', boxSizing: 'border-box',
            padding: '14px 20px', borderRadius: 12,
            background: 'transparent', color: 'var(--indipia-purple-600)',
            border: '1.5px solid var(--shell-border)',
            fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}
        >
          웹에서 계속하기
        </button>
      </div>
    </div>
  );
}
